import { useContractRosterMemberQuery } from '@access-cms/api-client';

import { InfoIcon } from '../../../components/Icon';
import Tooltip from '../../../components/Tooltip';
import { formatDate, normalizeTestId } from '../../../utils';
import type { RosterEntry } from '../types';

interface RosterExpandedRowProps {
  data: RosterEntry;
  contractId: string;
  rowIndex: number;
}

function RosterExpandedRow({ data, contractId, rowIndex }: RosterExpandedRowProps) {
  const baseTestId = 'roster-expanded-row';
  const rowTestId = normalizeTestId(`${baseTestId}-${data.userId ?? rowIndex}`);
  const { data: detailData } = useContractRosterMemberQuery(contractId, data.userId, {
    enabled: Boolean(contractId && data.userId),
  });
  const detailIsUsCitizen = detailData?.userProfile?.isUsCitizen;

  const displayData: RosterEntry = {
    ...data,
    email: detailData?.userProfile?.email ?? data.email,
    euaId: detailData?.workforceIdentity?.euaId,
    // An explicit null means unknown; only an omitted answer keeps the list value.
    isUSCitizen: detailIsUsCitizen === undefined ? data.isUSCitizen : detailIsUsCitizen,
    sixMonthsOrLess: detailData?.isSeasonal ?? data.sixMonthsOrLess,
    vettingEndDate: detailData?.vettingCompletedAt ? formatDate(detailData.vettingCompletedAt) : data.vettingEndDate,
    vettingStartDate: detailData?.dateAdded ? formatDate(detailData.dateAdded) : data.vettingStartDate,
  };

  return (
    <div
      className='access-table__expanded-content'
      data-testid={rowTestId}>
      <div className='access-table__expanded-content-row'>
        <div
          className='access-table__expanded-item'
          data-testid={`${rowTestId}-user-id`}>
          <span
            className='access-table__expanded-label'
            data-testid={`${rowTestId}-user-id-label`}>
            User ID
          </span>
          <span
            className='access-table__expanded-value'
            data-testid={`${rowTestId}-user-id-value`}>
            {displayData.euaId ?? 'Not available'}
          </span>
        </div>
        <div
          className='access-table__expanded-item'
          data-testid={`${rowTestId}-email`}>
          <span
            className='access-table__expanded-label'
            data-testid={`${rowTestId}-email-label`}>
            Email Address
          </span>
          <span
            className='access-table__expanded-value'
            data-testid={`${rowTestId}-email-value`}>
            {displayData.email}
          </span>
        </div>
        <div
          className='access-table__expanded-item'
          data-testid={`${rowTestId}-us-citizen`}>
          <span
            className='access-table__expanded-label'
            data-testid={`${rowTestId}-us-citizen-label`}>
            U.S. Citizen?
          </span>
          <span
            className='access-table__expanded-value'
            data-testid={`${rowTestId}-us-citizen-value`}>
            {displayData.isUSCitizen === true ? 'Yes' : displayData.isUSCitizen === false ? 'No' : 'Not provided'}
          </span>
        </div>
        <div
          className='access-table__expanded-item'
          data-testid={`${rowTestId}-six-months`}>
          <span
            className='access-table__expanded-label'
            data-testid={`${rowTestId}-six-months-label`}>
            Six Months or Less?
            <Tooltip
              className='roster-six-months-tooltip'
              placement='top'
              title='Indicates whether the individual has been in their current position for six months or less. This is important for determining vetting requirements and security clearance timelines.'>
              <InfoIcon />
            </Tooltip>
          </span>
          <span
            className='access-table__expanded-value'
            data-testid={`${rowTestId}-six-months-value`}>
            {displayData.sixMonthsOrLess ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
      <div className='access-table__expanded-content-row'>
        <div
          className='access-table__expanded-item'
          data-testid={`${rowTestId}-risk-level`}>
          <span
            className='access-table__expanded-label'
            data-testid={`${rowTestId}-risk-level-label`}>
            Risk Level
          </span>
          <span
            className='access-table__expanded-value'
            data-testid={`${rowTestId}-risk-level-value`}>
            {displayData.riskLevel}
          </span>
        </div>
        <div
          className='access-table__expanded-item'
          data-testid={`${rowTestId}-vetting-start`}>
          <span
            className='access-table__expanded-label'
            data-testid={`${rowTestId}-vetting-start-label`}>
            Vetting Start Date
          </span>
          <span
            className='access-table__expanded-value'
            data-testid={`${rowTestId}-vetting-start-value`}>
            {displayData.vettingStartDate}
          </span>
        </div>
        <div
          className='access-table__expanded-item'
          data-testid={`${rowTestId}-vetting-end`}>
          <span
            className='access-table__expanded-label'
            data-testid={`${rowTestId}-vetting-end-label`}>
            Vetting End Date
          </span>
          <span
            className='access-table__expanded-value'
            data-testid={`${rowTestId}-vetting-end-value`}>
            {displayData.vettingEndDate}
          </span>
        </div>
        <div className='access-table__expanded-item' />
      </div>
    </div>
  );
}

export default RosterExpandedRow;
